'use client';

import { forwardRef } from 'react';
import { Link as IntlLink } from '@/i18n/navigation';
import { cn } from '@/components/ui/_lib/cn';
import {
  buttonLinkClass,
  type ButtonVariantProp,
} from '@/components/ui/_lib/buttonVariants';
import Anchor, { type AnchorProps } from '@/components/ui/typography/Anchor';

export type AppLinkProps = AnchorProps & {
  href: string;
  external?: boolean;
  /** `true` | `'primary'` | `'ghost'` — renders as `.btn` (not `.anchor`). */
  btn?: ButtonVariantProp;
  disabled?: boolean;
  /** Native tooltip (`title` attribute) on the anchor. */
  title?: string;
  /** Target locale for `next-intl` `Link` (e.g. language menu). */
  locale?: string;
  hrefLang?: string;
};

/**
 * Same surface as `@/components/ui` `Link`, but internal routes use `next-intl` navigation
 * so `/services` becomes `/en/services` (or `/ar/...`) under `[locale]` routing.
 */
const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(
  (
    {
      href,
      external,
      className,
      children,
      disabled,
      btn,
      skeleton,
      skeletonMaxWaitMs,
      skeletonLines,
      title,
      ...props
    },
    ref,
  ) => {
    const isExternal = external || href.startsWith('http');
    const linkClass = cn(
      buttonLinkClass(btn, className),
      disabled && 'pointer-events-none opacity-50',
    );

    if (isExternal) {
      return (
        <Anchor
          ref={ref}
          href={href}
          className={linkClass}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={disabled}
          title={title}
          skeleton={skeleton}
          skeletonMaxWaitMs={skeletonMaxWaitMs}
          skeletonLines={skeletonLines}
          {...props}
        >
          {children}
        </Anchor>
      );
    }

    return (
      <IntlLink
        ref={ref}
        href={disabled ? '#' : href}
        className={linkClass}
        aria-disabled={disabled}
        title={title}
        {...props}
      >
        {children}
      </IntlLink>
    );
  },
);

AppLink.displayName = 'AppLink';

export default AppLink;
